/**
 * Pure query-shape helpers for the Algolia-backed calibre LISTING.
 *
 * Sibling to `calibreSearchQuery.ts` (which serves the pickers) and
 * `brandListQuery.ts` (which serves the brand listing). Kept out of `algolia.ts`
 * so the parameter building and the hit → row mapping can be unit-tested without
 * pulling in the Algolia client — and the `import.meta.env` credential read that
 * happens at its module scope.
 *
 * The pickers and the listing want DIFFERENT projections of the same index,
 * which is why this is a separate module rather than a wider
 * `CALIBRE_SEARCH_ATTRIBUTES`: a picker renders a name and a maker and then
 * hydrates, so shipping it the listing's twenty-odd attributes on every
 * keystroke would be payload nobody reads.
 */

import { Timestamp } from 'firebase/firestore';
import type { CalibreImage, CustomCalibreEntry } from './firebase';

/**
 * Attributes the calibre listing renders per row.
 *
 * The last six — `imageUrl`, `hasImage`, `source`, `createdAt`,
 * `additionalManufacturerIds` and `deprecated` — landed with watchlock #865,
 * which is what made this listing expressible in Algolia at all. Before them the
 * Image column, both image-related stats, the Source badge and pill, the Created
 * sort and the generic/non-generic Type filter all read fields the index did not
 * carry, so the page had to load all ~1,822 calibre documents from Firestore on
 * every mount.
 *
 * Requesting an attribute the index doesn't hold is silently dropped rather than
 * an error, so this list is only as good as the indexer it mirrors
 * (`buildCalibreIndexRecord`, watchlock `functions/src/entityIndexes.ts`) —
 * verified against that builder rather than assumed.
 */
export const CALIBRE_LIST_ATTRIBUTES = [
  'objectID',
  'name',
  'alsoKnownAs',
  'brandId',
  'brandName',
  'manufacturerId',
  'additionalManufacturerIds',
  // Shared-types 1.143.0. Dropped silently until the indexer emits it — the
  // listing then just behaves as primary-only.
  'originalManufacturerId',
  'familyId',
  'movementType',
  'isInHouse',
  'jewels',
  'powerReserveHours',
  'referenceCount',
  'linkedReferenceCount',
  'unlinkedReferenceCount',
  'source',
  'deprecated',
  'hasImage',
  'imageUrl',
  'createdAt',
] as const;

/**
 * Fields the listing used to render, or would like to, that the index does NOT
 * carry — so each is either gone from the table or comes from a Firestore read
 * of the individual document.
 *
 * Documented rather than merely implied, because each one is a silently blank
 * cell or a silently destructive write if a future caller assumes a row is a
 * whole calibre:
 *
 * - `data.frequencyVph` — the old Freq column. `buildCalibreIndexRecord` derives
 *   only the boolean `isHighBeat` (>= 28,800 vph) from it and indexes no number,
 *   so the exact beat rate is unavailable to a listing row. The column is now
 *   `Refs` (`referenceCount`, which IS indexed); the beat rate is still on the
 *   edit form.
 * - `images[]` — the index carries ONE resolved `imageUrl` for the thumbnail,
 *   never the array. `bulkSetCalibreImageSource` REWRITES that array, so it must
 *   only ever be handed hydrated documents.
 * - `data.*` beyond the four specs above (tiers, complications, dimensions,
 *   accuracy, …), `description`, `sourceUrl`, `aliasDetails`, `isCatchAll` — the
 *   whole of what the editor and the AI-enhance tool write back.
 *
 * Note also that the indexer resolves `imageUrl` / `hasImage` from the DOC-level
 * `images` field only. A legacy calibre whose pictures live solely under
 * `data.images` reads as image-less here, where the Firestore listing showed a
 * thumbnail — visible as a lower "With images" count, not as a broken row.
 */
export const CALIBRE_LIST_FIELDS_NOT_INDEXED = [
  'data.frequencyVph',
  'images',
  'data',
  'description',
  'sourceUrl',
  'aliasDetails',
  'isCatchAll',
] as const;

/** One `watchkeeper_calibres` record, as the listing consumes it. */
export interface CalibreListingHit {
  objectID: string;
  name?: string;
  alsoKnownAs?: string[];
  brandId?: string;
  brandName?: string;
  manufacturerId?: string;
  additionalManufacturerIds?: string[];
  originalManufacturerId?: string;
  familyId?: string;
  movementType?: string;
  isInHouse?: boolean;
  jewels?: number;
  powerReserveHours?: number;
  referenceCount?: number;
  linkedReferenceCount?: number;
  unlinkedReferenceCount?: number;
  source?: string;
  deprecated?: boolean;
  hasImage?: boolean;
  /** Ready-to-use absolute URL, resolved by the indexer. Absent = no image. */
  imageUrl?: string;
  /** Epoch milliseconds — the indexer flattens Firestore Timestamps. */
  createdAt?: number;
}

/**
 * One row of the calibre listing.
 *
 * Deliberately its OWN type rather than a partially-filled `CustomCalibreEntry`.
 * A row is a projection: it has no `images` array, no `data` beyond four specs,
 * no `aliasDetails`. Typing it as an entry would let it flow into
 * `bulkSetCalibreImageSource` or the editor's save path, where the missing
 * fields would be written back as blanks over real values. Every action that
 * touches those fields hydrates the row to a document first.
 */
export interface CalibreListRow {
  id: string;
  name: string;
  brandId?: string;
  brandName?: string;
  alsoKnownAs?: string[];
  /** Nullable to match `CustomCalibreEntry`, so the shared classifier accepts a row. */
  manufacturerId?: string | null;
  additionalManufacturerIds?: string[] | null;
  /** The maker that originally made it, when that differs (shared-types 1.143.0). */
  originalManufacturerId?: string | null;
  familyId?: string;
  movementType?: string;
  isInHouse?: boolean;
  jewels?: number;
  powerReserveHours?: number;
  /** Always a number — the indexer defaults it to 0, so the column never blanks. */
  referenceCount: number;
  /**
   * The linked / text-only split of `referenceCount`. Both absent until the
   * server has recounted this calibre under the union semantics.
   */
  linkedReferenceCount?: number;
  unlinkedReferenceCount?: number;
  source?: string;
  /** Always resolved: absent on the record means "not deprecated". */
  deprecated: boolean;
  hasImage: boolean;
  /** Set on the Algolia path — render it directly, no Storage round-trip. */
  imageUrl?: string;
  /**
   * Set on the FIRESTORE FALLBACK path only, where there is no pre-resolved URL
   * and the thumbnail has to turn a `storagePath` into a download URL itself.
   */
  image?: CalibreImage;
  createdAt?: Timestamp;
}

/**
 * How many records a single Algolia query can return, total, across all pages.
 *
 * This is `paginationLimitedTo`, which the calibres index leaves at Algolia's
 * DEFAULT of 1,000 (verified against the live index: page 1 of a
 * `hitsPerPage: 1000` query comes back `nbPages: 0` with an explanatory
 * `message`, not an error). The index holds 1,823 records, so a plain paged
 * loop would read `nbPages: 1`, stop after the first page, and silently return
 * 55% of the catalogue as if it were all of it. That is what
 * {@link CALIBRE_PARTITION_FACETS} exists to work around.
 */
export const CALIBRE_PAGINATION_LIMIT = 1000;

/**
 * Boolean attributes used to cut the index into buckets each small enough for
 * one query to return in full.
 *
 * Every one is written UNCONDITIONALLY by `buildCalibreIndexRecord` as a plain
 * `x === true` — never omitted — which is the property the whole scheme rests
 * on: `X:true` and `X:false` PARTITION the index, so concatenating the two
 * buckets loses nothing and duplicates nothing. An attribute that were merely
 * usually present (`source`, `familyId`) would drop every record missing it.
 *
 * Order matters only for efficiency — the fetch splits on one facet at a time
 * and stops as soon as a bucket fits. `isInHouse` leads because it is the
 * evenest split of the current data (799 / 1,024) and the oldest of these
 * attributes, so it is the least likely to have a record the backfill missed;
 * `isChronograph` then brings the larger half to 255 / 769. Six dimensions give
 * 64 buckets, which is ample headroom.
 *
 * All six are declared in `attributesForFaceting` (`hasImage` and `deprecated`
 * as `filterOnly`, which still filters — it only suppresses facet COUNTS).
 *
 * The right long-term fix is one line of watchlock: raise `paginationLimitedTo`
 * on `watchkeeper_calibres`. This is what lets the admin move off Firestore
 * today without waiting for that deploy.
 */
export const CALIBRE_PARTITION_FACETS = [
  'isInHouse',
  'isChronograph',
  'isHighBeat',
  'hasImage',
  'isGMT',
  'deprecated',
] as const;

/**
 * How many records the assembled listing may fall short of the index-wide count
 * before it is treated as broken rather than as raced.
 *
 * The buckets are fetched one after another, so a calibre DELETED midway
 * legitimately leaves the total one short of the count taken at the start. A
 * gap bigger than a few records means something structural — a record missing
 * one of the partition attributes, so it lands in neither half — and the caller
 * must fall back to Firestore rather than show a catalogue with holes in it.
 */
export const CALIBRE_PARTITION_DRIFT_TOLERANCE = 5;

/**
 * Algolia search parameters for one bucket of the calibre listing.
 *
 * `facetFilters` are the partition filters ONLY (see
 * {@link CALIBRE_PARTITION_FACETS}) — each its own group, so they AND together
 * into a single disjoint slice of the index. The listing's own filters (brand,
 * generic/non-generic, deprecated) are NOT here: the page holds every record, so
 * those cost no round trip at all, and the stat pills computed over that set
 * count exactly the rows the table is showing. A `facets: ['source']` response
 * would instead report index-wide totals that disagree with the list the moment
 * "show deprecated" is off. Same reasoning `buildBrandListParams` documents.
 *
 * Always `page: 0` — depth pagination is precisely what is unavailable here.
 *
 * `attributesToHighlight: []` because no listing surface renders highlight
 * markup. `analytics: false` keeps admin browsing out of the app's search
 * analytics. `cacheable: false` bypasses the SDK's in-memory response cache, so
 * Refresh after an edit shows the edit rather than replaying the pre-edit
 * response.
 */
export function buildCalibreListParams(
  facetFilters: readonly string[],
  hitsPerPage: number,
): Record<string, unknown> {
  const params: Record<string, unknown> = {
    hitsPerPage,
    page: 0,
    analytics: false,
    cacheable: false,
    attributesToRetrieve: [...CALIBRE_LIST_ATTRIBUTES],
    attributesToHighlight: [],
  };
  if (facetFilters.length > 0) {
    params.facetFilters = facetFilters.map((f) => [f]);
  }
  return params;
}

/** Trim a string attribute, treating the indexer's `''` default as absent. */
function present(value: string | undefined): string | undefined {
  const trimmed = (value ?? '').trim();
  return trimmed ? trimmed : undefined;
}

/**
 * Map one Algolia hit onto a listing row.
 *
 * `buildCalibreIndexRecord` writes several string attributes UNCONDITIONALLY
 * with an `''` default (`brandId`, `brandName`, `manufacturerId`,
 * `movementType`), so emptiness — not absence — is what "unset" looks like on a
 * hit. Those are normalised away here; leaving them through would make
 * `brandKeyOf` return `''` for a brand-less calibre in one path and `undefined`
 * in the other, and would give every unset calibre a `manufacturerId` the
 * classifier then tried to resolve.
 *
 * `createdAt` is rebuilt as a `Timestamp` from the indexed epoch millis, which
 * is what keeps the Added column's `.toDate()` and the Created sort working on a
 * hit-derived row exactly as they did on a Firestore document.
 */
export function calibreHitToRow(hit: CalibreListingHit): CalibreListRow {
  const row: CalibreListRow = {
    id: hit.objectID,
    name: present(hit.name) ?? hit.objectID,
    referenceCount:
      typeof hit.referenceCount === 'number' ? hit.referenceCount : 0,
    deprecated: hit.deprecated === true,
    hasImage: hit.hasImage === true,
  };
  if (typeof hit.linkedReferenceCount === 'number') row.linkedReferenceCount = hit.linkedReferenceCount;
  if (typeof hit.unlinkedReferenceCount === 'number') row.unlinkedReferenceCount = hit.unlinkedReferenceCount;
  const brandId = present(hit.brandId);
  if (brandId) row.brandId = brandId;
  const brandName = present(hit.brandName);
  if (brandName) row.brandName = brandName;
  if (hit.alsoKnownAs && hit.alsoKnownAs.length > 0) {
    row.alsoKnownAs = hit.alsoKnownAs;
  }
  const manufacturerId = present(hit.manufacturerId);
  if (manufacturerId) row.manufacturerId = manufacturerId;
  if (hit.additionalManufacturerIds && hit.additionalManufacturerIds.length > 0) {
    row.additionalManufacturerIds = hit.additionalManufacturerIds;
  }
  const originalManufacturerId = present(hit.originalManufacturerId);
  if (originalManufacturerId) row.originalManufacturerId = originalManufacturerId;
  const familyId = present(hit.familyId);
  if (familyId) row.familyId = familyId;
  const movementType = present(hit.movementType);
  if (movementType) row.movementType = movementType;
  if (hit.isInHouse !== undefined) row.isInHouse = hit.isInHouse;
  if (typeof hit.jewels === 'number') row.jewels = hit.jewels;
  if (typeof hit.powerReserveHours === 'number') {
    row.powerReserveHours = hit.powerReserveHours;
  }
  const source = present(hit.source);
  if (source) row.source = source;
  const imageUrl = present(hit.imageUrl);
  if (imageUrl) row.imageUrl = imageUrl;
  if (typeof hit.createdAt === 'number') {
    row.createdAt = Timestamp.fromMillis(hit.createdAt);
  }
  return row;
}

/**
 * Map a Firestore document onto the same row shape — the FALLBACK path, used
 * when Algolia is unconfigured or the listing search throws.
 *
 * Kept in the same module as `calibreHitToRow` on purpose: the two must agree
 * field for field, or the page silently renders differently depending on which
 * source answered. The one deliberate divergence is the thumbnail — a document
 * carries an `images` array whose first usable entry may be a `storagePath`, and
 * resolving that to a download URL is an async Storage call, so the row hands
 * the raw `CalibreImage` to the thumbnail instead of a URL.
 *
 * Image lookup checks the doc-level `images` first and then `data.images`, which
 * is what the pre-Algolia listing did. The indexer only reads the former, so
 * this path can show a thumbnail where the Algolia path shows none.
 */
export function calibreDocToRow(entry: CustomCalibreEntry): CalibreListRow {
  const specs = entry.data ?? {};
  const images = entry.images ?? specs.images ?? [];
  const image = images.find((img) => img?.storagePath || img?.url);
  const row: CalibreListRow = {
    id: entry.id,
    name: present(entry.name) ?? entry.id,
    referenceCount:
      typeof entry.referenceCount === 'number' ? entry.referenceCount : 0,
    deprecated: entry.deprecated === true,
    hasImage: images.length > 0,
  };
  if (typeof entry.linkedReferenceCount === 'number') row.linkedReferenceCount = entry.linkedReferenceCount;
  if (typeof entry.unlinkedReferenceCount === 'number') row.unlinkedReferenceCount = entry.unlinkedReferenceCount;
  const brandId = present(entry.brandId);
  if (brandId) row.brandId = brandId;
  const brandName = present(entry.brandName);
  if (brandName) row.brandName = brandName;
  if (entry.alsoKnownAs && entry.alsoKnownAs.length > 0) {
    row.alsoKnownAs = entry.alsoKnownAs;
  }
  const manufacturerId = present(entry.manufacturerId ?? undefined);
  if (manufacturerId) row.manufacturerId = manufacturerId;
  if (entry.additionalManufacturerIds && entry.additionalManufacturerIds.length > 0) {
    row.additionalManufacturerIds = entry.additionalManufacturerIds;
  }
  const originalManufacturerId = present(entry.originalManufacturerId ?? undefined);
  if (originalManufacturerId) row.originalManufacturerId = originalManufacturerId;
  const familyId = present(entry.familyId ?? undefined);
  if (familyId) row.familyId = familyId;
  const movementType = present(specs.movementType);
  if (movementType) row.movementType = movementType;
  if (specs.isInHouse !== undefined) row.isInHouse = specs.isInHouse;
  if (typeof specs.jewels === 'number') row.jewels = specs.jewels;
  if (typeof specs.powerReserveHours === 'number') {
    row.powerReserveHours = specs.powerReserveHours;
  }
  const source = present(entry.source);
  if (source) row.source = source;
  if (image) row.image = image;
  if (entry.createdAt) row.createdAt = entry.createdAt;
  return row;
}

/**
 * The listing's lenient brand key: canonical `brandId` when there is one, else
 * the legacy free-text `brandName`.
 *
 * Drives the Group dropdown, the brand filter, the brand sort and the grouped
 * section headers, so it lives here where both row builders and the tests can
 * see it — the whole point is that a row from Algolia and a row from Firestore
 * produce the SAME key for the same calibre.
 */
export function calibreRowBrandKey(row: {
  brandId?: string;
  brandName?: string;
}): string {
  return (row.brandId && row.brandId.trim()) || (row.brandName ?? '').trim();
}

/** What the Refs cell shows for one calibre. */
export interface CalibreRefsCell {
  /** Non-variant references linked by ID or matched by name/alias. */
  count: number;
  /** References matched by text only — still to be linked. 0 = nothing to do. */
  unlinked: number;
  /** Hover text for the warning badge; null when there is no badge. */
  tooltip: string | null;
}

/**
 * The Refs column as an action indicator: the count, plus how many of those
 * references match this calibre by text but are not linked to it by ID.
 *
 * No badge unless the server has actually computed the split — a calibre the
 * recount has not visited yet has no `unlinkedReferenceCount`, and showing that
 * as "clean" would be a claim nobody has checked.
 */
export function describeCalibreRefs(row: {
  referenceCount: number;
  linkedReferenceCount?: number;
  unlinkedReferenceCount?: number;
}): CalibreRefsCell {
  const count = row.referenceCount || 0;
  const unlinked =
    typeof row.unlinkedReferenceCount === 'number' && row.unlinkedReferenceCount > 0
      ? row.unlinkedReferenceCount
      : 0;
  if (unlinked === 0) return { count, unlinked: 0, tooltip: null };
  const linked =
    typeof row.linkedReferenceCount === 'number' ? row.linkedReferenceCount : count - unlinked;
  return {
    count,
    unlinked,
    tooltip: `${linked} linked by ID, ${unlinked} matched by text only. Run Reapply to link them.`,
  };
}

/** Manufacturer-filter value for "calibres with no manufacturer at all". */
export const NO_MANUFACTURER_FILTER = '__no_manufacturer__';

/**
 * Every manufacturer a calibre is attributed to: the primary first, then the
 * co-manufacturers, blanks dropped and duplicates collapsed.
 *
 * The same whole-set view `classifyCalibreGeneric` takes for the Type filter —
 * a co-developed calibre belongs to each of its makers.
 */
export function calibreRowManufacturerIds(row: {
  manufacturerId?: string | null;
  additionalManufacturerIds?: string[] | null;
}): string[] {
  const ids: string[] = [];
  for (const raw of [row.manufacturerId, ...(row.additionalManufacturerIds ?? [])]) {
    const id = typeof raw === 'string' ? raw.trim() : '';
    if (id && !ids.includes(id)) ids.push(id);
  }
  return ids;
}

/**
 * The listing's Manufacturer filter: `''` passes everything,
 * {@link NO_MANUFACTURER_FILTER} passes calibres attributed to nobody, and a
 * manufacturer id passes every calibre associated with the maker — primary,
 * co-developer or original maker — the same set as the index's combined
 * `allManufacturerIds` (plain Option A, James 2026-11-03: ETA 6498-1 lists
 * under ETA and Unitas; no strict mode).
 */
export function calibreMatchesManufacturerFilter(
  row: {
    manufacturerId?: string | null;
    additionalManufacturerIds?: string[] | null;
    originalManufacturerId?: string | null;
  },
  filter: string,
): boolean {
  if (!filter) return true;
  const ids = calibreRowManufacturerIds(row);
  if (filter === NO_MANUFACTURER_FILTER) return ids.length === 0;
  return ids.includes(filter) || row.originalManufacturerId === filter;
}

/**
 * The capacity badge for a row seen on maker `browsing`: "now ETA" on a calibre
 * Unitas originally made (it is on Unitas's page because of that), and
 * "originally Unitas" on a calibre ETA makes now that someone else originated.
 * Null when neither applies.
 */
export function originalMakerBadge(
  row: { manufacturerId?: string | null; originalManufacturerId?: string | null },
  browsing: string,
): { kind: 'now' | 'originally'; manufacturerId: string } | null {
  const primary = row.manufacturerId ?? '';
  const original = row.originalManufacturerId ?? '';
  if (!browsing || !original || original === primary) return null;
  if (original === browsing && primary) return { kind: 'now', manufacturerId: primary };
  if (primary === browsing) return { kind: 'originally', manufacturerId: original };
  return null;
}


export interface ManufacturerFilterOption {
  id: string;
  label: string;
  /** Every calibre associated with it: made, co-developed, or originally made. */
  count: number;
}

/**
 * Options for the Manufacturer filter: the manufacturers that have at least one
 * calibre in `rows`, by label, each with its count, plus how many rows have
 * none.
 *
 * An id the catalogue does not know (deleted, or not indexed yet) is listed
 * under the raw id rather than dropped — otherwise its calibres could not be
 * reached through the filter at all. Counts overlap by design: a co-developed
 * calibre is counted under each of its makers, so they do not sum to the total.
 */
export function buildManufacturerFilterOptions(
  rows: readonly {
    manufacturerId?: string | null;
    additionalManufacturerIds?: string[] | null;
    originalManufacturerId?: string | null;
  }[],
  nameById: ReadonlyMap<string, string>,
): { noManufacturerCount: number; options: ManufacturerFilterOption[] } {
  const counts = new Map<string, number>();
  let noManufacturerCount = 0;
  for (const row of rows) {
    const ids = calibreRowManufacturerIds(row);
    if (ids.length === 0) noManufacturerCount += 1;
    // The original maker counts too, so a historical maker with no calibre of
    // its own (Unitas) still gets an option, matching the filter.
    const orig = typeof row.originalManufacturerId === 'string' ? row.originalManufacturerId.trim() : '';
    for (const id of orig && !ids.includes(orig) ? [...ids, orig] : ids) counts.set(id, (counts.get(id) ?? 0) + 1);
  }
  const options = Array.from(counts, ([id, count]) => ({
    id,
    label: nameById.get(id) || id,
    count,
  })).sort((a, b) => a.label.localeCompare(b.label) || a.id.localeCompare(b.id));
  return { noManufacturerCount, options };
}

/**
 * Splits the listing on the deprecated flag — the toggle ISOLATES rather than
 * includes.
 *
 * Deprecated calibres are redirect aliases (reclassified into a tier of another
 * calibre). Folding them into the alphabetical list of 2,500+ rows made them
 * technically visible and practically unfindable: a faded "P" name sits at
 * position 2,500 with nothing to scroll to. The counted label reads as a filter,
 * so the toggle behaves like one — on shows the deprecated set alone, off shows
 * everything else.
 *
 * The two branches are complementary, so no row can be in both and none can be
 * in neither.
 */
export function filterCalibresByDeprecated<T extends { deprecated: boolean }>(
  rows: T[],
  onlyDeprecated: boolean,
): T[] {
  return rows.filter((r) => r.deprecated === onlyDeprecated);
}

/**
 * Add every row's id to the selection, or remove all of them when they are
 * already selected in full.
 *
 * One helper for BOTH selection scopes on the calibre listing — the header
 * checkbox, which acts on the whole filtered set across every page, and the
 * footer's "Select page", which acts on the current slice only. The two differ
 * solely in the rows handed to them, so sharing the toggle is what stops them
 * drifting into different ideas of what unticking means: it removes only the
 * ids in `rows`, leaving a selection built up in the other scope untouched.
 *
 * An empty `rows` is a no-op rather than a clear — `every()` is vacuously true
 * on it, and treating that as "all selected, so deselect" would make the
 * control wipe the selection on a page that has nothing to offer.
 */
export function toggleRowsInSelection(
  selected: ReadonlySet<string>,
  rows: readonly { id: string }[],
): Set<string> {
  const next = new Set(selected);
  if (rows.length === 0) return next;
  const allSelected = rows.every((r) => next.has(r.id));
  for (const r of rows) {
    if (allSelected) next.delete(r.id);
    else next.add(r.id);
  }
  return next;
}
